import React from 'react';

/**
 * Spirify Mascot - Hibou sage et mignon 🦉
 * Sans animation sautillante - statique mais expressif
 * Now with translations support for all languages
 */

const OwlMascot = ({ score, className = '' }) => {
  const isHappy = score >= 70;
  const isSuperHappy = score >= 90;
  
  return (
    <svg 
      viewBox="0 0 200 200" 
      className={`drop-shadow-lg ${className}`}
    >
      {/* Aura douce */}
      <ellipse cx="100" cy="105" rx="90" ry="85" fill="rgba(255,255,255,0.15)"/>
      
      {/* Corps du hibou */}
      <ellipse cx="100" cy="130" rx="55" ry="60" fill="#8B6914"/>
      
      {/* Ventre */}
      <ellipse cx="100" cy="140" rx="38" ry="42" fill="#F5DEB3"/>
      <ellipse cx="100" cy="145" rx="30" ry="32" fill="#FAEBD7"/>
      
      {/* Motif du ventre */}
      <path d="M 85 125 Q 100 130 115 125" stroke="#D2B48C" strokeWidth="2" fill="none"/>
      <path d="M 80 140 Q 100 145 120 140" stroke="#D2B48C" strokeWidth="2" fill="none"/>
      <path d="M 82 155 Q 100 160 118 155" stroke="#D2B48C" strokeWidth="2" fill="none"/>
      
      {/* Tête */}
      <ellipse cx="100" cy="70" rx="48" ry="42" fill="#8B6914"/>
      
      {/* Oreilles/Plumes */}
      <path d="M 55 45 Q 60 25 75 40 Q 65 50 55 45" fill="#6B4F12"/>
      <path d="M 145 45 Q 140 25 125 40 Q 135 50 145 45" fill="#6B4F12"/>
      
      {/* Face claire */}
      <ellipse cx="100" cy="72" rx="38" ry="32" fill="#FAEBD7"/>
      
      {/* Contour des yeux */}
      <ellipse cx="78" cy="68" rx="20" ry="18" fill="#F5DEB3"/>
      <ellipse cx="122" cy="68" rx="20" ry="18" fill="#F5DEB3"/>
      
      {/* Yeux */}
      <ellipse cx="78" cy="68" rx="14" ry="15" fill="white"/>
      <ellipse cx="122" cy="68" rx="14" ry="15" fill="white"/>
      
      {/* Pupilles */}
      <ellipse cx="80" cy="70" rx="8" ry="9" fill="#2C1810"/>
      <ellipse cx="120" cy="70" rx="8" ry="9" fill="#2C1810"/>
      
      {/* Reflets */}
      <circle cx="76" cy="65" r="4" fill="white"/>
      <circle cx="116" cy="65" r="4" fill="white"/>
      <circle cx="82" cy="73" r="2" fill="white"/>
      <circle cx="122" cy="73" r="2" fill="white"/>
      
      {/* Sourcils expressifs */}
      {isSuperHappy ? (
        <>
          <path d="M 60 52 Q 78 48 96 55" stroke="#5D4E37" strokeWidth="3" fill="none" strokeLinecap="round"/>
          <path d="M 104 55 Q 122 48 140 52" stroke="#5D4E37" strokeWidth="3" fill="none" strokeLinecap="round"/>
        </>
      ) : isHappy ? (
        <>
          <path d="M 62 55 Q 78 52 94 55" stroke="#5D4E37" strokeWidth="2.5" fill="none" strokeLinecap="round"/>
          <path d="M 106 55 Q 122 52 138 55" stroke="#5D4E37" strokeWidth="2.5" fill="none" strokeLinecap="round"/>
        </>
      ) : null}
      
      {/* Joues roses */}
      <ellipse cx="55" cy="78" rx="8" ry="5" fill="#FFB6C1" opacity="0.6"/>
      <ellipse cx="145" cy="78" rx="8" ry="5" fill="#FFB6C1" opacity="0.6"/>
      
      {/* Bec */}
      <path d="M 100 82 L 92 95 L 100 92 L 108 95 Z" fill="#FF8C00"/>
      
      {/* Sourire selon le score */}
      {isSuperHappy ? (
        <path d="M 88 100 Q 100 112 112 100" stroke="#5D4E37" strokeWidth="2.5" fill="none" strokeLinecap="round"/>
      ) : isHappy ? (
        <path d="M 90 100 Q 100 108 110 100" stroke="#5D4E37" strokeWidth="2" fill="none" strokeLinecap="round"/>
      ) : (
        <path d="M 93 100 Q 100 105 107 100" stroke="#5D4E37" strokeWidth="2" fill="none" strokeLinecap="round"/>
      )}
      
      {/* Ailes */}
      <ellipse cx="48" cy="120" rx="12" ry="30" fill="#6B4F12"/>
      <ellipse cx="152" cy="120" rx="12" ry="30" fill="#6B4F12"/>
      
      {/* Pattes */}
      <ellipse cx="82" cy="188" rx="12" ry="8" fill="#FF8C00"/>
      <ellipse cx="118" cy="188" rx="12" ry="8" fill="#FF8C00"/>
      
      {/* Griffes */}
      <path d="M 74 190 L 72 196" stroke="#CC7000" strokeWidth="2" strokeLinecap="round"/>
      <path d="M 82 192 L 82 198" stroke="#CC7000" strokeWidth="2" strokeLinecap="round"/>
      <path d="M 90 190 L 92 196" stroke="#CC7000" strokeWidth="2" strokeLinecap="round"/>
      <path d="M 110 190 L 108 196" stroke="#CC7000" strokeWidth="2" strokeLinecap="round"/>
      <path d="M 118 192 L 118 198" stroke="#CC7000" strokeWidth="2" strokeLinecap="round"/>
      <path d="M 126 190 L 128 196" stroke="#CC7000" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  );
};

const Mascot = ({ score, isVisible, onClose, t }) => {
  if (!isVisible) return null;

  const getReaction = () => {
    if (score >= 90) return 'celebration';
    if (score >= 70) return 'happy';
    if (score >= 50) return 'encouraging';
    return 'supportive';
  };

  const reaction = getReaction();

  // Use translations if available, otherwise fallback to French
  const reactions = {
    celebration: {
      message: t?.lesson?.mascotCelebration || 'INCROYABLE !',
      subMessage: t?.lesson?.mascotCelebrationSub || 'Tu es un champion !',
      bgColor: 'from-yellow-400 to-amber-500'
    },
    happy: {
      message: t?.lesson?.mascotHappy || 'BRAVO !',
      subMessage: t?.lesson?.mascotHappySub || 'Excellent travail !',
      bgColor: 'from-emerald-400 to-green-500'
    },
    encouraging: {
      message: t?.lesson?.mascotEncouraging || 'BIEN JOUÉ !',
      subMessage: t?.lesson?.mascotEncouragingSub || 'Continue comme ça !',
      bgColor: 'from-blue-400 to-indigo-500'
    },
    supportive: {
      message: t?.lesson?.mascotSupportive || 'BON EFFORT !',
      subMessage: t?.lesson?.mascotSupportiveSub || 'Tu vas y arriver !',
      bgColor: 'from-purple-400 to-pink-500'
    }
  };

  const currentReaction = reactions[reaction];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div 
        className={`relative bg-gradient-to-br ${currentReaction.bgColor} rounded-3xl p-8 shadow-2xl`}
        style={{ maxWidth: '380px' }}
      >
        <div className="flex flex-col items-center">
          {/* Hibou mascotte - SANS animation */}
          <div className="relative">
            <OwlMascot score={score} className="w-40 h-44" />
            
            {/* Petites étoiles décoratives */}
            <span className="absolute -top-2 -left-2 text-xl">✨</span>
            <span className="absolute -top-2 -right-2 text-xl">✨</span>
            {reaction === 'celebration' && (
              <>
                <span className="absolute top-1/3 -left-5 text-lg">⭐</span>
                <span className="absolute top-1/3 -right-5 text-lg">⭐</span>
              </>
            )}
          </div>

          {/* Message */}
          <div className="text-center mt-3">
            <h2 className="text-3xl font-bold text-white mb-2 drop-shadow-lg">
              {currentReaction.message}
            </h2>
            <p className="text-xl text-white/90 mb-1">
              {currentReaction.subMessage}
            </p>
            <p className="text-5xl font-bold text-white mt-2">
              {score}%
            </p>
          </div>

          {/* Bouton continuer - toujours accessible */}
          <button
            onClick={onClose}
            className="mt-5 px-10 py-4 bg-white text-gray-800 font-bold text-lg rounded-full shadow-lg hover:bg-gray-100 active:scale-95 transition-all"
          >
            {t?.lesson?.mascotContinue || 'Continuer'} →
          </button>
        </div>
      </div>
    </div>
  );
};

export { OwlMascot };
export default Mascot;
